import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';

interface BusinessCategory {
  id: number;
  category_code: string;
  category_name: string;
  description: string;
  icon: string;
  sort_order: number;
}

interface BusinessCategorySelectorProps {
  selectedCategoryId: number | null;
  onSelect: (categoryId: number) => void;
}

const Container = styled.div`
  width: 100%;
  margin: 20px 0;
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #718096;
  margin-bottom: 20px;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 16px;
`;

const CategoryCard = styled.div<{ selected: boolean }>`
  border: 2px solid ${props => props.selected ? '#4299e1' : '#e2e8f0'};
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.selected ? '#ebf8ff' : '#ffffff'};

  &:hover {
    border-color: #4299e1;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const CategoryIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

const CategoryDescription = styled.div`
  font-size: 13px;
  color: #718096;
  line-height: 1.5;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #718096;
`;

const ErrorMessage = styled.div`
  background-color: #fed7d7;
  color: #c53030;
  padding: 12px;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const BusinessCategorySelector: React.FC<BusinessCategorySelectorProps> = ({
  selectedCategoryId,
  onSelect
}) => {
  const [categories, setCategories] = useState<BusinessCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      const response = await axios.get(`${API_URL}/business/categories`);

      if (response.data.success) {
        setCategories(response.data.data);
      } else {
        setError('Failed to load business categories');
      }
    } catch (err: any) {
      console.error('Error fetching business categories:', err);
      setError(err.response?.data?.message || 'Failed to load business categories');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading business categories...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Select Your Business Type</Title>
      <Subtitle>
        Choose the category that best describes your business. This will set up the appropriate 
        chart of accounts for your organization.
      </Subtitle>

      <CategoriesGrid>
        {categories.map((category) => (
          <CategoryCard
            key={category.id}
            selected={selectedCategoryId === category.id}
            onClick={() => onSelect(category.id)}
          >
            <CategoryIcon>{category.icon}</CategoryIcon>
            <CategoryName>{category.category_name}</CategoryName>
            <CategoryDescription>{category.description}</CategoryDescription>
          </CategoryCard>
        ))}
      </CategoriesGrid>
    </Container>
  );
};

export default BusinessCategorySelector;
